import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { mergeMap, map, shareReplay, tap, distinct } from 'rxjs/operators';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import { environment } from '../../environments/environment';
import { IBid, Bid } from '../models/IBid';
import { ICost, Cost, ICostByFieldDuration } from '../models/ICost';
import { ICurrencyConversionModel } from '../models/ICurrencyConversionModel';
import { IBidResponse, ISampleBlendType, BidResponse } from '../models/IBidResponse';
import { Utils } from '../Utils'
import { ISupplierMap } from '../models/ISupplier';
import { ISample } from '../models/ISample';
import { ISampleBlend } from '../models/ISampleBlend';
import { IADTemplate } from "../models/IADTemplate";
import { BidMode } from "../enums/bid-mode.enum";
import { IBeatLog } from '../models/IBeatLog';
import { BidDTO } from '../models/IBidDTO';
import { IAudienceDesignerRedirectDTO } from '../models/IAudienceDesignerRedirectDTO';
import { IProfileRate } from '../models/IProfileRate';
import { ITemplateCriteria } from '../models/ITemplateCriteria';
import { ICpiRange } from '../models/ICpiRange';
import { ITemplateSupplierRateCardModel } from '../models/ITemplateSupplierRateCardModel';
import { IPerformanceIndicator } from '../models/IPerformanceIndicators';
import { IPerformanceChartItem, IPerformanceChartSupplierMap, IPerformanceChartTimingItem } from '../models/IPerformanceChart';
import { IaudienceDesignerUrlDto } from '../models/IAudienceDesignerUrlDTO';
import { LogDTO } from '../models/ILogDTO';
import { IFeasibilityCountReportResult } from '../models/IFeasibilityCountReportResult';
import { IPerformanceSettingsModel } from '../models/IPerformanceSettingsModel';
import { IPanelType } from '../models/IPanelType';
import { IVirtualPanelImportLog } from '../models/IVirtualPanelImportLog';
import { ICapacitySource } from '../models/ICapacitySource';
import { IRelatedBid } from '../models/IRelatedBid';
import { Item, IItem } from '../models/IItem';
import { ITrackerDevianceModel } from '../models/ITrackerDevianceModel';
import { IFizzyAnswer } from '../models/IFizzyAnswer';
import { IFizzyQuestion } from '../models/IFizzyQuestion';
import { ICountry } from '../models/ICountry';
import { IPanelCountryLanguage } from '../models/IPanelCountryLanguage';
import { IRecruitmentVariable } from '../models/IRecruitmentVariable';
import { CookieService } from 'ngx-cookie-service';

@Injectable()
export class FezaDataService {
  fezaUrl: string;
  supplierUrl: string;
  fileConfigBasePath: string;
  private readonly _answerCache = new Map<number, Observable<IFizzyAnswer>>();
  private readonly _questionCache = new Map<number, Observable<IFizzyQuestion>>();
  private readonly _currencyConversionCache = new Map<string, Observable<ICurrencyConversionModel>>();

  constructor(private cookieService: CookieService,
    private http: HttpClient) {
    this.fezaUrl = environment.fezeUrl;
    this.supplierUrl = environment.supplierUrl;
    this.fileConfigBasePath = environment.fileConfigBasePath;
  }

  public ProcessfeasibilityRequest(bid: IBid, correlationId: string): Observable<IBidResponse> {
    if (bid.feasibilities) {
      for (let f of bid.feasibilities) {
        f.sampleName = f.sampleName || f.countryCode;

        if (f.quotaTables.length === 0 && f.target.length === 0)
        {
          //the backend now requires a target or quota, so the test UI creates one.
          f.target.push({
            questions: [
              {
                id: -1,
                answers: [{ id: -1, rangeStart: "1", rangeEnd: "100", listId: null, text: null, answerText: null, listIdLastUpdatedDate:null }],
                operator: "OR",
                responseCode: "Ok",
                name: "Age Range"
              },
            ],
            isSampleUniverse: false
          });
        }
      }
    }

    return this.http.post<IBidResponse>(this.fezaUrl + "/api/v1/Feasibility/RequestFeasibility", bid, this.getFezaHeaderAndOptions(correlationId))
      .pipe(map(resp => this.processResponse(resp, correlationId)));
  }

  public GetResponse(bidId: string, currency: string, bypassMongo: boolean = false): Observable<IBidResponse> {
    var correlationId = Utils.generateUUID();
    return this.http.get<IBidResponse>(this.fezaUrl + `/api/v1/Feasibility/FeasibilityResponse?bidRequestId=${bidId}&displayCurrency=${currency || ""}&rnd=${Math.random()}&bypassMongo=${bypassMongo}`, this.getFezaHeaderAndOptions(correlationId))
      .pipe(map(resp => this.processResponse(resp, correlationId)));
  }

  private processResponse(response: IBidResponse, correlationId: string): IBidResponse {
    response._correlationId = correlationId;
    if (response.request && response.request.feasibilities) {
      for (let f of response.request.feasibilities) {
        f._daysInField = Math.floor(f.hoursInField / 24);
        f._hoursInField = Math.round(f.hoursInField % 24);
        f.sampleName = f.sampleName || f.countryCode;
      }
    }
    for (let key in response.feasibilities) {
      response.feasibilities[key] = response.feasibilities[key].sort((x, y) => x.supplierId - y.supplierId);
      for (let sample of response.feasibilities[key]) {
        var numberOfCompletes = parseInt(sample.numberOfCompletes === "max" ? "10000" : sample.numberOfCompletes);

        if (response.request && response.request.feasibilities) {
          for (let f of response.request.feasibilities) {
            if (f.feasibilityId === sample.feasibilityId)
              numberOfCompletes = parseInt(f.numberOfCompletes);//get the actual requested amount to figure out the target count
          }
        }
        sample.costs = sample.costs || {} as ICostByFieldDuration;

        sample.feasibilityId = sample.feasibilityId || key;
        for (let qt of (sample.quotaTables || []))
          for (let q of (qt.quotas || [])) {
            q._targetCount = Math.round(numberOfCompletes * (q.percentage / 100.0)); // I want the complete count here as simple field.  :-P
            for (let dur in (q.completes || [])) {
              // we don't show the decimals in the UI with a format filter, but I want the <input> to have the data rounded to make it look nice when editing too.  
              q.completes[dur] = Math.round(q.completes[dur]);
            }
            qt.tableName = qt.tableName || "";
          }
        for (let dur in (sample.supplierTotalCompletes || [])) {
          sample.supplierTotalCompletes[dur] = Math.round(sample.supplierTotalCompletes[dur]);
        }
      }
    }
    return response;
  }

  public SaveSupplierFeasibilityResponse(sample: ISample): Observable<boolean> {
    var correlationId = Utils.generateUUID();
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/Feasibility/SupplierFeasibilityResponse?feasibilityId=${sample.feasibilityId}`, sample, this.getFezaHeaderAndOptions(correlationId));
  }


  public CheckSuppliers(bid: IBid): Observable<ISupplierMap> {
    return this.http.post<ISupplierMap>(this.fezaUrl + "/api/v1/Feasibility/CheckSuppliers", bid, this.getFezaHeaderAndOptions());
  }

  public GetTemplateSampleBlends(bid: IBid): Observable<ISampleBlendType> {
    return this.http.post<ISampleBlendType>(this.fezaUrl + "/api/v1/Feasibility/SampleBlend", bid, this.getFezaHeaderAndOptions());
  }

  public SaveBlend(blend: ISampleBlend, feasibilityId: string, autoFill: boolean): Observable<void> {
    var correlationId = Utils.generateUUID();
    return this.http.put<void>(this.fezaUrl + `/api/v1/Feasibility/SampleBlend?feasibilityId=${feasibilityId}&autoFillUnderallocatedBlend=${autoFill}`, blend, this.getFezaHeaderAndOptions(correlationId));
  }


  public GetAdhocBlend(bid: IBidResponse, request: IBid, feasibilityId: string): Observable<ISampleBlend> {
    //if the request had no quotas, the response will contain one new quota that has a blank name which is a required field to send back.  doh!  remove it.
    let clonedBid = Object.assign(new BidResponse(), JSON.parse(JSON.stringify(bid)));
    let clonedRequest = Object.assign(new Bid(), JSON.parse(JSON.stringify(request)));
    for (let key in clonedBid.feasibilities) {
      for (let sample of clonedBid.feasibilities[key]) {
        sample.quotaTables = (sample.quotaTables || []).filter(x => (x.tableName || "") !== "");
      }
    }
    clonedBid.request = clonedRequest;
    return this.http.post<ISampleBlend>(this.fezaUrl + `/api/v1/Feasibility/GenerateSampleBlend?feasibilityId=${feasibilityId}`, clonedBid, this.getFezaHeaderAndOptions());
  }

  public DefaultDropoutRate(countryCode: string, languages: string[], lengthOfInterview: number, researchType: string): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/TemplateSearch/AbandonRate?CountryCode=${countryCode}&Languages=${languages}&ResearchType=${researchType}&LengthOfInterview=${lengthOfInterview}`, this.getFezaHeaderAndOptions());
  }

  public DefaultSniffoutRate(countryCode: string, researchType: string, languages: string[], allowMobile: boolean, allowTablet: boolean, allowDesktop: boolean): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/TemplateSearch/SniffoutRate?CountryCode=${countryCode}&languages=${languages}&researchType=${researchType}&AllowMobile=${allowMobile}&AllowTablet=${allowTablet}&AllowDesktop=${allowDesktop}`, this.getFezaHeaderAndOptions());
  }

  public getAdTemplates(countryCode: string, languages: string[], researchType: string): Observable<IADTemplate[]> {
    return this.http.get<IADTemplate[]>(this.fezaUrl + `/api/v2/AudienceDesignerTemplate/TemplateSearch?countryCode=${countryCode}&locales=${languages}&researchType=${researchType}`, this.getFezaHeaderAndOptions());
  }

  public getADTemplate(id: number, locale: string): Observable<IADTemplate> {
    return this.http.get<IADTemplate>(this.fezaUrl + `/api/v2/AudienceDesignerTemplate/TemplateSearchById?templateId=${id}&locales=${locale}`, this.getFezaHeaderAndOptions());
  }

  public getPerformanceIndicators(): Observable<Map<string, IPerformanceIndicator>> {
    return this.http.get<Map<string, IPerformanceIndicator>>(this.fezaUrl + `/api/v2/Performance/Indicators`, this.getFezaHeaderAndOptions());
  }

  public getPerformanceChartOverallByHour(): Observable<IPerformanceChartItem[]> {
    return this.http.get<IPerformanceChartItem[]>(this.fezaUrl + `/api/v2/Performance/PerformanceChartOverallByHour`, this.getFezaHeaderAndOptions());
  }

  public getPerformanceChartOverallByDay(): Observable<IPerformanceChartItem[]> {
    return this.http.get<IPerformanceChartItem[]>(this.fezaUrl + `/api/v2/Performance/PerformanceChartOverallByDay`, this.getFezaHeaderAndOptions());
  }

  public getPerformanceChartOverallByDayBySupplier(): Observable<IPerformanceChartSupplierMap> {
    return this.http.get<IPerformanceChartSupplierMap>(this.fezaUrl + `/api/v2/Performance/PerformanceChartOverallByDayBySupplier`, this.getFezaHeaderAndOptions());
  }

  public getPerformanceChartOverallTimingBreakdown(): Observable<IPerformanceChartTimingItem[]> {
    return this.http.get<IPerformanceChartTimingItem[]>(this.fezaUrl + `/api/v2/Performance/PerformanceChartOverallTimingBreakdown`, this.getFezaHeaderAndOptions());
  }

  public getRateCardsAllTargets(criteria: ITemplateCriteria, currency: string): Observable<ICpiRange[]> {
    return this.http.post<ITemplateSupplierRateCardModel[]>(this.fezaUrl + `/api/v1/TemplateSearch/RateCardForAllTargets?currencyCode=${currency}`, criteria, this.getFezaHeaderAndOptions())
      .pipe(map(x =>
        x.map(rateCard => {
          return {
            cpi: rateCard.cpi,
            completesBetween: [rateCard.numberOfCompletesStart, rateCard.numberOfCompletesEnd],
            researchTypes: rateCard.researchTypes,
            sampleCategories: rateCard.sampleCategories
          } as ICpiRange;
        })));
  }

  public getSampleBlendTemplates(ids: number[]): Observable<ISampleBlend[]> {
    let calls = [];
    for (let i = 0; i < ids.length; i++) {
      calls.push(this.getSampleBlendTemplate(ids[i]));
    }
    if (calls.length === 0)
      return Observable.of([]);
    return Observable.forkJoin(calls);
  }

  public getSampleBlendTemplate(id: number): Observable<ISampleBlend> {
    return this.http.get<ISampleBlend>(this.fezaUrl + `/api/v1/TemplateSearch/BlendTemplateById?templateId=${id}`, this.getFezaHeaderAndOptions());
  }


  private getFezaHeaderAndOptions(correlationId: string = null) {
    return {
      headers: new HttpHeaders({ 'X-Correlation-ID': correlationId == null ? Utils.generateUUID() : correlationId })
    };
  }

  getTestBid(): Observable<IBid> {
    return this.http.get<IBid>('assets/data/request.json');
  }

  getTestBidResponse(): Observable<IBidResponse> {
    return this.http.get<IBidResponse>('assets/data/response.json');
  }

  public UploadFile(url: string, data: FormData): Observable<string> {
    return this.http.post<string>(url, data, this.getFezaHeaderAndOptions());
  }

  public DropCache(url: string): Observable<string> {
    return this.http.post<string>(url, this.getFezaHeaderAndOptions());
  }

  public setDefaultFactorsForSample(sample: ISample): Observable<ISample> {
    return forkJoin(
      this.DefaultSniffoutRate(sample.countryCode, sample.researchType, sample.countryCultures, sample.allowMobile, sample.allowTablet, sample.allowDesktop),
      this.DefaultDropoutRate(sample.countryCode, sample.countryCultures, sample.lengthOfInterview, sample.researchType)
    ).map((data) => {
      let sniffout = data[0] as number;
      let abandon = data[1] as number;
      sample.sniffoutRatePercent = sniffout;
      sample.abandonRatePercent = abandon;
      return sample;
    });
  }

  public SampleBlendByBidMode(bidId: string, fezaId: string, bidMode: BidMode): Observable<ISampleBlend> {
    return this.http.get<ISampleBlend>(this.fezaUrl + `/api/v1/Feasibility/SampleBlendByBidMode?bidId=${bidId}&feasibilityId=${fezaId}&mode=${bidMode}`, this.getFezaHeaderAndOptions());
  }

  public getSamplePreCommissioningBlends(bidId: string, fezaIds: string[]): Observable<ISampleBlend[]> {
    let calls = [];
    for (let i = 0; i < fezaIds.length; i++) {
      calls.push(this.SampleBlendByBidMode(bidId, fezaIds[i], BidMode.Precommissioning));
    }
    if (calls.length === 0)
      return Observable.of([]);
    return Observable.forkJoin(calls);
  }

  public GetSupportEmail(): Observable<string> {
    return this.http.get<string>(this.fezaUrl + "/api/v1/FileConfiguration/SupportEmail", this.getFezaHeaderAndOptions());
  }

  public SetSupportEmail(email: string): Observable<boolean> {
    var correlationId = Utils.generateUUID();
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/SupportEmail?emailAddress=${email}`, this.getFezaHeaderAndOptions(correlationId));
  }

  public GetPanelVariables(countryCode: string): Observable<IRecruitmentVariable[]> {
    return this.http.get<IRecruitmentVariable[]>(this.fezaUrl + `/api/v3/InternalPanel/recruitmentreportvariables?countryCode=${countryCode}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetAgeBreaks(countryCode: string): Observable<Array<{ item1: number; item2: number }>> {
    return this.http.get<Array<{ item1: number; item2: number }>>(this.fezaUrl + `/api/v3/InternalPanel/recruitmentReportAgeBreaks?countryCode=${countryCode}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public SaveAgeBreaks(countryCode: string, ageBreaks: Array<{ item1: number; item2: number }>) : Observable<boolean>
  {
    return this.http.post<boolean>(this.fezaUrl +  `/api/v3/InternalPanel/recruitmentReportAgeBreaks?countryCode=${countryCode}`, ageBreaks, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public SaveRecruitmentReportChanges(countryCode: string, variables:string[]): Observable<IRecruitmentVariable[]> {
    return this.http.post<IRecruitmentVariable[]>(this.fezaUrl + `/api/v3/InternalPanel/recruitmentreportvariables?countryCode=${countryCode}`, variables, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public SendSupplierEmail(bidId: string, fezeId: string, supplierId: number, email: string, name: string): Observable<string> {
    return this.http.post<string>(this.fezaUrl + `/api/v1/Feasibility/SupplierEmail?bidId=${bidId}&feasibilityId=${fezeId}&supplierId=${supplierId}&email=${email}&name=${encodeURIComponent(name)}&send=false&asHtml=false`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  private readonly emailCookieName: string = "email";

  public SupplierFeasibilitiesEmail(bidId: string, fezeId: string, supplierId: number, exportAllFesabilitiesForSupplier: boolean): Observable<boolean> {
    console.log(this.cookieService.get(this.emailCookieName));
    return this.http.post<boolean>(this.fezaUrl + `/api/v1/Feasibility/SupplierFeasibilitiesEmail?email=${this.cookieService.get(this.emailCookieName)}&bidId=${bidId}&feasibilityId=${fezeId}&supplierId=${supplierId}&exportAllFesabilitiesForSupplier=${exportAllFesabilitiesForSupplier}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public CurrencyConversion(from: string, to: string, value: number): Observable<ICurrencyConversionModel> {
    //  to skip call caching to this:
    //  return this.http.get<ICurrencyConversionModel>(this.fezaUrl + `/api/v1/currency/currencyconversion?baseCurrency=${from}&targetCurrency=${to}&value=${value}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
    if (from === to || value === 0) {
      let rate: ICurrencyConversionModel = { currencyCode: to, value: value, conversionRate: 1.0 };
      return Observable.of(rate);
    }
    //we ask for the same data over, so shareReplay is used to reuse the same call
    let key = `${to}-${from}-${value}`;
    if (!this._currencyConversionCache.get(key)) {
      this._currencyConversionCache.set(key, this.http.get<ICurrencyConversionModel>(this.fezaUrl + `/api/v1/currency/currencyconversion?baseCurrency=${from}&targetCurrency=${to}&value=${value}`, this.getFezaHeaderAndOptions()).pipe(shareReplay(1)));
    }
    return this._currencyConversionCache.get(key);
  }

  public CostConversion(cost: ICost, toCurrency: string): Observable<ICost> {
    return this.CurrencyConversion(cost.currency, toCurrency, 100).pipe(map(currencyConversionModel => {
      let rate = (currencyConversionModel && currencyConversionModel.conversionRate) ? currencyConversionModel.conversionRate : 0;
      //lets make a copy of the inputCurrency and convert all the values in by the rate
      let tmp: ICost = Object.assign(new Cost(), JSON.parse(JSON.stringify(cost)));
      tmp.currency = toCurrency;
      tmp.cpi = (tmp.cpi || 0) * rate;
      if ((tmp.cpiRanges || []).length > 0) {
        for (let i = 0; i < tmp.cpiRanges.length; i++) {
          tmp.cpiRanges[i].cpi = (tmp.cpiRanges[i].cpi || 0) * rate;
        }
      }
      return tmp;
    }));
  }

  public GetBeats(): Observable<IBeatLog[]> {
    return this.http.get<IBeatLog[]>(this.fezaUrl + "/api/v1/Beat/BeatsStatuses", this.getFezaHeaderAndOptions());
  }

  public GetBeat(name: string): Observable<IBeatLog> {
    return this.http.get<IBeatLog>(this.fezaUrl + `/api/v1/Beat/BeatStatus?name=${name}`, this.getFezaHeaderAndOptions());
  }

  public GetPanelProfile(countryCode: string, standardQuestionId: number): Observable<IProfileRate> {
    return this.http.get<IProfileRate>(this.fezaUrl + `/api/v2/InternalPanel/ProfileRate/${standardQuestionId}/${countryCode}`, this.getFezaHeaderAndOptions());
  }

  public RunBeatIfNotRunning(name: string): Observable<IBeatLog> {
    return this.GetBeat(name).pipe(mergeMap(beat => {
      if (beat.status === "Running") {
        beat.error += "Beat already running; execution not requested.";
        return Observable.of(beat);//if it is running... don't run it again!
      };
      return this.RunBeat(name).pipe(mergeMap(ran => {
        return this.GetBeat(name);
      }));
    }));
  }

  private RunBeat(name: string): Observable<any> {
    return this.http.put(this.fezaUrl + `/api/v1/Beat/Execution?name=${name}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }


  public AdRedirect(redirectInfo: BidDTO, correlationId: string): Observable<IAudienceDesignerRedirectDTO> {
    return this.http.put<IAudienceDesignerRedirectDTO>(this.fezaUrl + "/api/v1/Feasibility/Bid", redirectInfo, this.getFezaHeaderAndOptions(correlationId));
  }

  public AdRedirectByBidId(redirectInfo: IaudienceDesignerUrlDto, redirectBidId: string, correlationId: string): Observable<IAudienceDesignerRedirectDTO> {
    return this.http.put<IAudienceDesignerRedirectDTO>(this.fezaUrl + `/api/v1/AudienceDesigner/BidRedirect/${redirectBidId}/Redirect`, redirectInfo, this.getFezaHeaderAndOptions(correlationId));
  }

  public FizzyRedirectByBidId(redirectBidId: string, correlationId: string) {

  }


  public GetBidModeWaitTime(mode: string): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/FileConfiguration/SupplierWaitTime?bidMode=${mode}`, this.getFezaHeaderAndOptions());
  }

  public SetBidModeWaitTime(mode: string, seconds: number): Observable<boolean> {
    var correlationId = Utils.generateUUID();
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/SupplierWaitTime?bidMode=${mode}&seconds=${seconds}`, this.getFezaHeaderAndOptions(correlationId));
  }

  public CheckBidExists(bidId: string): Observable<boolean> {
    return this.http.get<boolean>(this.fezaUrl + `/api/v1/Test/BidExists?bidId=${bidId}`, this.getFezaHeaderAndOptions());
  }

  public GetVirtalPanels(): Observable<string[]> {
    return this.http.get<string[]>(this.fezaUrl + `/api/v2/internalpanel/virtualpanels`, this.getFezaHeaderAndOptions());
  }

  public getPanelCountries(): Observable<IPanelCountryLanguage[]>
  {
    //TODO make these distinct by country name
    var lst = this.http.get<IPanelCountryLanguage[]>(this.fezaUrl + `/api/v3/internalpanel/panelCountries`, this.getFezaHeaderAndOptions()).pipe(
      map(arr=> {
        let st=[] as Array<IPanelCountryLanguage>;
        arr = arr.filter((val, indx, arr) => { return arr.map(item => item.countryCode).indexOf(val.countryCode) === indx; });
        arr.sort((a,b)=> {
          if (a.countryCode < b.countryCode) return -1;
          if (a.countryCode > b.countryCode) return 1;
        })
        return arr;
      })    
    );
    
    return lst;
  }

  public GetLogs(correlationId?: string): Observable<LogDTO[]> {
    if (correlationId !== undefined)
      return this.http.get<LogDTO[]>(this.fezaUrl + `/api/v1/Report/GetLogs?correlationId=` + correlationId, this.getFezaHeaderAndOptions());    
  }


  public GetGreenFlagCounts(url: string): Observable<IFeasibilityCountReportResult> {
    return this.http.get<IFeasibilityCountReportResult>(url, this.getFezaHeaderAndOptions());
  }

  public GetPerfromanceSettings(): Observable<IPerformanceSettingsModel> {
    return this.http.get<IPerformanceSettingsModel>(this.fezaUrl + `/api/v1/FileConfiguration/PerformanceSettings`, this.getFezaHeaderAndOptions());
  }

  public SetPerfromanceSettings(setting: IPerformanceSettingsModel): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/PerformanceSettings`, setting, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetTrackerDeviations(): Observable<ITrackerDevianceModel> {
    return this.http.get<ITrackerDevianceModel>(this.fezaUrl + `/api/v1/FileConfiguration/TrackerDeviances`, this.getFezaHeaderAndOptions());
  }

  public SetTrackerDeviations(setting: ITrackerDevianceModel): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/TrackerDeviances`, setting, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetFezaCacheSetting(): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/FileConfiguration/FezaCacheTimeSetting`, this.getFezaHeaderAndOptions());
  }

  public SetFezaCacheSetting(setting: number): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/FezaCacheTimeSetting?setting=${setting}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetFezaApiTimeoutSetting(): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/FileConfiguration/FeasibilityCheckAPIWaitTime`, this.getFezaHeaderAndOptions());
  }

  public SetFezaApiTimeoutSetting(seconds: number): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/FeasibilityCheckAPIWaitTime?seconds=${seconds}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetEpcValue(): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/FileConfiguration/FloorEpcValueSetting`, this.getFezaHeaderAndOptions());
  }

  public SetEpcValue(setting: number): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/FloorEpcValueSetting?setting=${setting}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public SetFullyPopThreshold(setting: number): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/FullyPopThreshold?setting=${setting}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetFullyPopThreshold(): Observable<number> {
    return this.http.get<number>(this.fezaUrl + `/api/v1/FileConfiguration/FullyPopThreshold`, this.getFezaHeaderAndOptions());
  }


  public SetPanelCheckTargetingMode(setting: string): Observable<boolean> {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/PanelCheckTargetingMode?setting=${setting}`, this.getFezaHeaderAndOptions(Utils.generateUUID()));
  }

  public GetPanelCheckTargetingMode(): Observable<string> {
    return this.http.get<string>(this.fezaUrl + `/api/v1/FileConfiguration/PanelCheckTargetingMode`, this.getFezaHeaderAndOptions());
  }

  public getPanels(): Observable<IPanelType[]> {
    return this.http.get<IPanelType[]>(this.fezaUrl + `/api/v2/InternalPanel/panels`, this.getFezaHeaderAndOptions());
  }

  public getVirtualPanelImportLogs(): Observable<IVirtualPanelImportLog[]> {
    return this.http.get<IVirtualPanelImportLog[]>(this.supplierUrl + `/api/v1/panel/virtualpanel/logs?Page=1&PageCount=100&QueryBy=Failed&OrderBy=Recordstamp%20desc`, this.getFezaHeaderAndOptions());
  }

  public dropVirtualPanelLogFile(virtualPanelImportLogId: number): Observable<boolean> {
    return this.http.get<boolean>(this.supplierUrl + `/api/v1/panel/virtualpaneldropfile/${virtualPanelImportLogId}`, this.getFezaHeaderAndOptions());
  }

  public GetCapacitySources(): ICapacitySource[] {
    var capacitySourceTypes: ICapacitySource[] = [
      {
        code: "Internal",
        title: "Internal sample",
        description: "Ipsos internal sample"
      },

      {
        code: "01-FeAPI",
        title: "Feasibility API",
        description: "Supplier is providing a URL (endpoint) that can be accessed in an automated way to obtain real-time sample capacity (feasibility) results"
      },

      {
        code: "02-ISAY-MULTI",
        title: "Estimated Capacity (internal sample analogy)",
        description: "Supplier capacity is estimated based on their total capacity, assuming a similar age and gender profile as internal sample"
      },

      {
        code: "03-VirtualPanelWOCommitment",
        title: "Estimated capacity (virtual sample w/o commitment)",
        description: "Supplier capacity is estimated based on a static file upload of their general capacities on detailed age and gender. Supplier provided an estimated capacity and did not commit to deliver the capacities, so a feasibility check is needed before survey launch."
      },

      {
        code: "04-VirtualPanelWCommitment",
        title: "Estimated capacity (virtual sample with commitment)",
        description: "Supplier capacity is estimated based on a static file upload of their general capacities on detailed age and gender. Supplier commit to deliver the capacities."
      },

      {
        code: "05-VirtualPanelBallPark",
        title: "Estimated capacity (ballpark)",
        description: "Supplier capacity is estimated based on their total capacity, assuming an estimated profile on age and gender."
      },

      {
        code: "06-NoSource",
        title: "No Capacity Available",
        description: "This supplier is eligible for a project, but the supplier needs to be contacted to get results on a project by project basis"
      }
    ];
    return capacitySourceTypes;
  }

  public GetRelatedBids(exernalIquoteNumber): Observable<IRelatedBid[]> {
    return this.http.get<IRelatedBid[]>(this.fezaUrl + `/api/v2/Bids/${exernalIquoteNumber}/RelatedBids`, this.getFezaHeaderAndOptions());
  }

  public getBidReferenceClients(): Observable<Array<IItem>> {
    return this.http.get<IItem[]>(this.fezaUrl + `/api/v1/Common/LookupTable/BidReferenceClientLK`, this.getFezaHeaderAndOptions());
  }

  public SetPanelOneImportEnabledFlag(panelOneImportEnabledFlag: boolean)
  {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/PanelOneImportEnabledFlag?setting=${panelOneImportEnabledFlag}`, this.getFezaHeaderAndOptions());
  }

  public GetPanelOneImportEnabledFlag(){
    return this.http.get<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/PanelOneImportEnabledFlag`, this.getFezaHeaderAndOptions());
  }

  public SetFeApiFallBackCallsFlag(feApiFallBackCallsEnabled: boolean)
  {
    return this.http.put<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/FeApiFallBackSolutionFlag?setting=${feApiFallBackCallsEnabled}`, this.getFezaHeaderAndOptions());
  }

  public GetFeApiFallBackCallsFlag(){
    return this.http.get<boolean>(this.fezaUrl + `/api/v1/FileConfiguration/FeApiFallBackSolutionFlag`, this.getFezaHeaderAndOptions());
  }

  public DropVirtualPanelTable(type: string, id: string, country: string, parentGroupId: string) {
    if (type.toUpperCase() === "VIRTUALPANELGROUP")
      return this.http.delete<boolean>(this.supplierUrl + `/api/v1/panel/virtualpanelgroup/${country}/${id}`, this.getFezaHeaderAndOptions());
    else if (parentGroupId) {
      return this.http.delete<boolean>(this.supplierUrl + `/api/v1/panel/VirtualPanel/${country}/${id}?supplierGroupId=${parentGroupId}`, this.getFezaHeaderAndOptions());
    }
    else {
      return this.http.delete<boolean>(this.supplierUrl + `/api/v1/panel/VirtualPanel/${country}/${id}`, this.getFezaHeaderAndOptions());
    }
  }

  public GetS1RestrictedModeEnabled(): Observable<boolean> {
    return this.http.get<boolean>(this.fezaUrl + "/api/v1/FileConfiguration/S1RestrictedModeEnabled", this.getFezaHeaderAndOptions());
  }

  public GetAnswer(id: number): Observable<IFizzyAnswer> {

    if (!this._answerCache.get(id)) {
      //console.log(`Getting an answer here:${locale}`);
      this._answerCache.set(id, this.http.get<IFizzyAnswer>(this.fezaUrl + `/api/v1/Common/Answer/${id}`, this.getFezaHeaderAndOptions()).pipe(shareReplay(1)));
    }
    return this._answerCache.get(id);
  }

  public GetQuestion(id: number): Observable<IFizzyQuestion> {

    if (!this._questionCache.get(id)) {
      this._questionCache.set(id, this.http.get<IFizzyQuestion>(this.fezaUrl + `/api/v1/Common/Question/${id}`, this.getFezaHeaderAndOptions()).pipe(shareReplay(1)));
    }
    return this._questionCache.get(id);
  }

  getAnswersFromList(answerIds: Array<number>): Observable<IFizzyAnswer[]> {
    var answers = [];
    for (let id of answerIds)
      answers.push(this.GetAnswer(id));
    return Observable.forkJoin(answers).map((res => [].concat.apply([], res)));
  }

}



// WEBPACK FOOTER //
// ./src/app/services/fezadata.service.ts